import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { deleteOwnAccount } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "./profile-form";

const controlClass =
  "h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)";

export function DeleteAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");

  const reset = () => {
    setConfirmText("");
    setReason("");
    setPassword("");
  };

  const deleteMutation = useMutation({
    mutationFn: deleteOwnAccount,
    onSuccess: () => {
      toast.success(t`Your account has been deleted.`);
      reset();
      queryClient.clear();
      navigate({ to: "/login", search: { redirect: "/dashboard" } });
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : t`Failed to delete account`,
      );
    },
  });

  const canSubmit =
    confirmText.trim() === "DELETE" &&
    password.length > 0 &&
    !deleteMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Delete account</Trans></DialogTitle>
          <DialogDescription>
            <Trans>
              Permanently delete your account and remove access to your
              personal settings.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 rounded-lg bg-(--color-danger-bg) p-3">
            <TriangleAlert className="mt-0.5 size-5 shrink-0 text-(--color-danger-text)" />
            <div>
              <p className="text-sm font-medium text-(--color-danger-text)">
                <Trans>Attention</Trans>
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                <Trans>
                  This action is permanent. You will lose access to your
                  personal settings, and this cannot be undone.
                </Trans>
              </p>
            </div>
          </div>

          <Field label={t`Type DELETE to confirm`} htmlFor="delete-confirm">
            <Input
              id="delete-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className={controlClass}
            />
          </Field>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="delete-reason"
              className="text-xs font-medium text-(--color-text-primary)"
            >
              <Trans>Reason (optional)</Trans>
            </label>
            <textarea
              id="delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t`Share why you want to delete your account…`}
              className="min-h-32 w-full resize-none rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 py-2.5 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) focus-visible:border-(--color-ring)"
            />
          </div>

          <Field label={t`Confirm password`} htmlFor="delete-password">
            <Input
              id="delete-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t`Enter password to continue…`}
              className={controlClass}
            />
          </Field>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => deleteMutation.mutate()}
            disabled={!canSubmit}
          >
            {deleteMutation.isPending ? t`Deleting…` : t`Delete account`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
